import React from "react";
import { useNavigate } from "react-router-dom";
import "./Services.css";

const services = [
  {
    number: "01",
    slug: "google-search-ads",
    title: "Google Search Ads",
    description:
      "Capture homeowners searching for immediate, emergency repairs with high-intent keyword targeting and call-only extensions. We optimize negative keywords daily to eliminate wasted spend from low-intent and DIY queries.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none">
        <path
          d="M12 3L3 7.5L12 12L21 7.5L12 3Z"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path
          d="M5 10V15.5C5 17.5 8.1 20 12 20C15.9 20 19 17.5 19 15.5V10"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    ),
  },

  {
    number: "02",
    slug: "local-services-ads",
    title: "Local Services Ads (LSA)",
    description:
      "Dominate the top of Google with Google Guaranteed badge positioning. We manage verification, dispute invalid lead calls, and maintain top bidding placement to maximize qualified dispatch volume.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none">
        <path
          d="M4 19V5"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M4 17L9 12L13 15L20 7"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M16 7H20V11"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },

  {
    number: "03",
    slug: "meta-lead-ads",
    title: "Meta lead ads",
    description:
      "Generate high-margin planned replacement leads for HVAC installs and basement waterproofing via targeted Facebook and Instagram video ads. Instant forms pre-qualify home ownership before dispatch.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none">
        <path
          d="M12 21C16.5 16.8 19 13.5 19 9.8C19 5.9 15.9 3 12 3C8.1 3 5 5.9 5 9.8C5 13.5 7.5 16.8 12 21Z"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <circle
          cx="12"
          cy="9.5"
          r="2.5"
          stroke="currentColor"
          strokeWidth="1.8"
        />
      </svg>
    ),
  },

  {
    number: "04",
    slug: "seo",
    title: "SEO",
    description:
      "Drive sustainable organic growth with strategic SEO. We target high-intent keywords, improve local rankings, and build long-term visibility that brings more qualified customers to your business.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none">
        <path
          d="M7 4H17C18.7 4 20 5.3 20 7V17C20 18.7 18.7 20 17 20H7C5.3 20 4 18.7 4 17V7C4 5.3 5.3 4 7 4Z"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <path
          d="M8 12C8.8 10.5 10 9.7 11.3 9.7C12.6 9.7 13.2 11 14.3 11C15.5 11 16 9.5 17 9"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    ),
  },

  {
    number: "05",
    slug: "web-development",
    title: "Web Development",
    description:
      "Fast, responsive, conversion-ready websites built to turn visitors into customers. Clear messaging, strong calls-to-action, and seamless performance across every device to generate more leads.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none">
        <rect
          x="3"
          y="4"
          width="18"
          height="16"
          rx="2"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <path
          d="M3 8H21"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <path
          d="M8 13L6 15L8 17"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 17H16"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    ),
  },

  {
    number: "06",
    slug: "ai-video-creation",
    title: "AI Video Creation",
    description:
      "Dynamic short-form video creative produced with AI avatars, voice synthesis, and automated copywriting to dominate local home service feeds across YouTube, Meta, and TikTok.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none">
        <rect
          x="3"
          y="5"
          width="18"
          height="14"
          rx="2"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <path
          d="M10 9L16 12L10 15V9Z"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
];

function Services() {
  const navigate = useNavigate();

  const handleServiceClick = (slug) => {
    navigate(`/services/${slug}`);
  };

  return (
    <section className="services-section" id="services">
      <div className="services-container">

        {/* =========================
            HEADING
        ========================= */}

        <div className="services-header">
          <span className="services-label">
            OUR SERVICES
          </span>

          <h2>
            Paid acquisition & AI growth systems built for{" "}
            <span>Home Services</span>
          </h2>

          <p>
            Every strategy is calibrated to capture high-intent
            emergency calls and high-margin replacement bookings.
          </p>
        </div>


        {/* =========================
            SERVICES GRID
        ========================= */}

        <div className="services-grid">

          {services.map((service) => (
            <div
              className="service-card"
              key={service.number}
              onClick={() => handleServiceClick(service.slug)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (
                  event.key === "Enter" ||
                  event.key === " "
                ) {
                  event.preventDefault();
                  handleServiceClick(service.slug);
                }
              }}
            >

              {/* CARD TOP */}

              <div className="service-card-top">

                <span className="service-number">
                  {service.number}
                </span>

                <div className="service-icon">
                  {service.icon}
                </div>

              </div>


              {/* CARD CONTENT */}

              <div className="service-content">

                <h3>
                  {service.title}
                </h3>

                <p>
                  {service.description}
                </p>

              </div>


              {/* OPEN INDICATOR */}

              

            </div>
          ))}

        </div>


        {/* =========================
            CTA
        ========================= */}

        <div className="services-cta">

          <div>
            <h3>
              Ready to Grow Your Business?
            </h3>

            <p>
              Let's build a strategy that brings you more
              visibility, leads, and customers.
            </p>
          </div>

          <a
            href="https://meetings-na2.hubspot.com/adwise-media?uuid=b5338f0a-6ee1-4e2b-88df-ab25699ff81c"
            target="_blank"
            rel="noopener noreferrer"
            className="services-btn"
          >
            Book a Free Consultation
            <span>↗</span>
          </a>

        </div>

      </div>
    </section>
  );
}

export default Services;